import { alphanumericKey } from '@numeo/types-core'
import type { AutofillValue } from './targets'

/** How a field shows a document's value: `filled` (green) holds the value the document put there; `differs` (orange) holds HR's own. */
export type AutofillFieldState = 'filled' | 'differs'

/** Case, spaces and punctuation do not make two values different ("12-3456789" = "123456789", "26,000" = 26000). */
const comparable = (value: unknown): string => alphanumericKey(String(value ?? ''))

export const isEmptyValue = (value: unknown): boolean => value === null || value === undefined || (typeof value === 'string' && !value.trim())

/**
 * Whether the form already holds the document's value. A masked number (`••••8306`) never does: the profile shows HR the full bank
 * number, so a mask is what the record itself holds, and the document's number is offered beside it.
 */
export function holdsValue(current: unknown, value: AutofillValue): boolean {
    if (isEmptyValue(current)) return false
    return comparable(current) === comparable(value)
}

interface AutofillPlan<K extends string> {
    /** Values to write into the form now. */
    fill: Partial<Record<K, AutofillValue>>
    /** Values the document gives for fields HR already filled differently: shown, never written until HR accepts one. */
    differs: Partial<Record<K, AutofillValue>>
}

/**
 * What a document's values do to the form. An empty field is filled. A field that still holds a value an earlier document filled
 * is filled again (HR has not touched it). A field that already holds the value is left alone. Any other field HR filled keeps
 * HR's value and the document's is offered beside it. A locked field is never written.
 */
export function planAutofill<K extends string>(
    read: Partial<Record<K, AutofillValue>>,
    current: (key: K) => unknown,
    filledBefore: Partial<Record<K, AutofillValue>>,
    locked: ReadonlySet<K> = new Set()
): AutofillPlan<K> {
    const plan: AutofillPlan<K> = { fill: {}, differs: {} }
    for (const key in read) {
        const value = read[key]
        if (value === undefined || locked.has(key)) continue
        const now = current(key)
        if (holdsValue(now, value)) continue
        const earlier = filledBefore[key]
        if (isEmptyValue(now) || (earlier !== undefined && holdsValue(now, earlier))) plan.fill[key] = value
        else plan.differs[key] = value
    }
    return plan
}

/** A value the card's documents give for a field, and the files that show it. */
export interface AutofillOffer {
    value: AutofillValue
    fileNames: string[]
}

/** What one file of a card read: its name and the form values it gives. */
export interface FileValues<K extends string> {
    fileName: string
    values: Partial<Record<K, AutofillValue>>
}

/** Whether two document values are the same value written differently ("12-3456789" and "123456789"). */
const sameValue = (a: AutofillValue, b: AutofillValue): boolean => comparable(a) === comparable(b)

/**
 * The values a card's files give, field by field. A field every file that shows it agrees on is `agreed`; a field the files disagree on
 * is a `conflict`, every value kept with its files, so HR picks one: neither is written.
 */
export function mergeFileValues<K extends string>(
    files: ReadonlyArray<FileValues<K>>
): {
    agreed: Partial<Record<K, AutofillValue>>
    conflicts: Partial<Record<K, AutofillOffer[]>>
} {
    const byField: Partial<Record<K, AutofillOffer[]>> = {}
    for (const { fileName, values } of files) {
        for (const key in values) {
            const value = values[key]
            if (value === undefined) continue
            const offers = (byField[key] ??= [])
            const same = offers.find((offer) => sameValue(offer.value, value))
            if (same) same.fileNames.push(fileName)
            else offers.push({ value, fileNames: [fileName] })
        }
    }
    const agreed: Partial<Record<K, AutofillValue>> = {}
    const conflicts: Partial<Record<K, AutofillOffer[]>> = {}
    for (const key in byField) {
        const offers = byField[key] ?? []
        if (offers.length === 1) agreed[key] = offers[0].value
        else if (offers.length > 1) conflicts[key] = offers
    }
    return { agreed, conflicts }
}

/**
 * How a field shows right now, computed from what the form holds (never stored): orange with the documents' values the field does not
 * hold yet, green while it holds the value a document filled. Typing a value clears its offer; typing over a filled value clears green.
 */
export function fieldAutofillState(
    current: unknown,
    filled: AutofillValue | undefined,
    offers: readonly AutofillOffer[] | undefined
): { state: 'differs'; offers: AutofillOffer[] } | { state: 'filled' } | undefined {
    // Offers first: a document only offers beside a value it did not fill (HR's, one HR accepted, or none when the files disagree), so a
    // newer document that disagrees with an accepted value shows orange rather than staying green.
    const open = (offers ?? []).filter((offer) => !holdsValue(current, offer.value))
    // An empty field shows its offers too: a field HR emptied after a document filled it is never filled again (`planCard`), so the
    // document's value waits beside it with Use, as the card's "check the highlighted fields" and the scroll to it promise.
    if (open.length > 0) return { state: 'differs', offers: open }
    if (filled !== undefined && holdsValue(current, filled)) return { state: 'filled' }
    return undefined
}
